import ApiService from "@/core/services/ApiService";
import JwtService from "@/core/services/JwtService";
import { Actions, Mutations } from "@/store/enums/StoreEnums";
import { normalizeError } from "@/store/helpers/errorHelper";
import { Module, Action, Mutation, VuexModule } from "vuex-module-decorators";
import { jwtDecode, JwtPayload } from "jwt-decode";

interface CustomJwtPayload extends JwtPayload {
  unique_name: string;
  email: string;
  role: string[] | string; // Can be a single string or an array
  exp: number;
}

export interface User {
  email: string;
  accessToken: string;
  roles: string[];
  unique_name: string;
  exp: number;
}

export interface UserAuthInfo {
  errors: unknown;
  user: User;
  isAuthenticated: boolean;
  twoFactorRequired: boolean;
  twoFactorMethod: string;
  twoFactorSessionToken: string;
  requiresTwoFactorSetup: boolean;
}

@Module({ namespaced: true, name: "AuthModule" })
export default class AuthModule extends VuexModule implements UserAuthInfo {
  errors = {};
  user = {} as User;
  isAuthenticated = false;
  twoFactorRequired = false;
  twoFactorMethod = "";
  twoFactorSessionToken = "";
  requiresTwoFactorSetup = false;

  /**
   * Get current user object
   * @returns User
   */
  get currentUser(): User {
    const accessToken = JwtService.getToken();
    if (!accessToken) return {} as User;

    try {
      const decodedToken = jwtDecode<CustomJwtPayload>(accessToken);
      return {
        email: decodedToken.email,
        accessToken: accessToken,
        roles: Array.isArray(decodedToken.role)
          ? decodedToken.role
          : [decodedToken.role],
        unique_name: decodedToken.unique_name,
        exp: decodedToken.exp,
      };
    } catch (error) {
      // Error intentionally not logged to prevent PII leakage
      return {} as User;
    }
  }

  /**
   * Verify user authentication
   * @returns boolean
   */
  get isUserAuthenticated(): boolean {
    if (this.isAuthenticated) {
      // check if token is expired
      const user = this.currentUser;
      const now = Date.now() / 1000; // Current time in seconds
      if (user.exp && user.exp < now) {
        return false;
      }
      return true;
    }

    return false;
  }

  get isTwoFactorRequired(): boolean {
    return this.twoFactorRequired;
  }

  get getTwoFactorMethod(): string {
    return this.twoFactorMethod;
  }

  get getError() {
    const [errorName] = Object.keys(this.errors);
    const error = this.errors[errorName];
    return error;
  }

  @Mutation
  [Mutations.SET_ERROR](errorObj) {
    this.errors = normalizeError(errorObj);
  }

  @Mutation
  [Mutations.SET_AUTH](data) {
    this.isAuthenticated = true;
    this.errors = {};
    JwtService.saveToken(data.accessToken);
  }

  @Mutation
  [Mutations.SET_TWO_FACTOR](data: {
    twoFactorMethod: string;
    twoFactorSessionToken: string;
    requiresTwoFactorSetup?: boolean;
  }) {
    this.twoFactorRequired = true;
    this.twoFactorMethod = data.twoFactorMethod;
    this.twoFactorSessionToken = data.twoFactorSessionToken;
    this.requiresTwoFactorSetup = data.requiresTwoFactorSetup || false;
    this.errors = {};
  }

  @Mutation
  [Mutations.CLEAR_TWO_FACTOR]() {
    this.twoFactorRequired = false;
    this.twoFactorMethod = "";
    this.twoFactorSessionToken = "";
    this.requiresTwoFactorSetup = false;
  }

  @Mutation
  [Mutations.PURGE_AUTH]() {
    this.isAuthenticated = false;
    this.user = {} as User;
    this.errors = [];
    this.twoFactorRequired = false;
    this.twoFactorMethod = "";
    this.twoFactorSessionToken = "";
    this.requiresTwoFactorSetup = false;
    JwtService.destroyAllTokens();
  }

  @Action
  [Actions.LOGIN](credentials) {
    return ApiService.post("/account/sign_in", credentials)
      .then(({ data }) => {
        if (data.requiresTwoFactorSetup) {
          console.log("requires 2fa setup");
          this.context.commit(Mutations.SET_TWO_FACTOR, {
            twoFactorMethod: "authenticator",
            twoFactorSessionToken: data.twoFactorSessionToken,
            requiresTwoFactorSetup: true,
          });
        } else if (data.requiresTwoFactor) {
          this.context.commit(Mutations.SET_TWO_FACTOR, {
            twoFactorMethod: data.twoFactorMethod,
            twoFactorSessionToken: data.twoFactorSessionToken,
          });
        } else {
          this.context.commit(Mutations.SET_AUTH, data);
        }
        this.context.commit(Mutations.SET_ERROR, null);
      })
      .catch(({ response, message }) => {
        this.context.commit(Mutations.SET_ERROR, { response, message });
      });
  }

  @Action
  async [Actions.REFRESH_TOKEN](): Promise<boolean> {
    try {
      const newAccessToken = await ApiService.refreshAccessToken();
      this.context.commit(Mutations.SET_AUTH, { accessToken: newAccessToken });
      return true;
    } catch {
      this.context.commit(Mutations.PURGE_AUTH);
      return false;
    }
  }

  @Action
  [Actions.VERIFY_TWO_FACTOR](payload: {
    code: string;
    twoFactorSessionToken: string;
  }) {
    return ApiService.post("/account/verify_two_factor", payload)
      .then(({ data }) => {
        this.context.commit(Mutations.SET_AUTH, data);
        this.context.commit(Mutations.CLEAR_TWO_FACTOR);
        this.context.commit(Mutations.SET_ERROR, null);
      })
      .catch(({ response, message }) => {
        this.context.commit(Mutations.SET_ERROR, { response, message });
      });
  }

  @Action
  async [Actions.SETUP_AUTHENTICATOR](twoFactorSessionToken: string) {
    try {
      const { data } = await ApiService.post("/account/setup_authenticator", {
        twoFactorSessionToken,
      });
      return data;
    } catch ({ response, message }: any) {
      this.context.commit(Mutations.SET_ERROR, { response, message });
      throw new Error();
    }
  }

  @Action
  [Actions.VERIFY_AUTHENTICATOR_SETUP](payload: {
    code: string;
    twoFactorSessionToken: string;
  }) {
    return ApiService.post("/account/verify_authenticator_setup", payload)
      .then(({ data }) => {
        this.context.commit(Mutations.SET_AUTH, data);
        this.context.commit(Mutations.CLEAR_TWO_FACTOR);
        this.context.commit(Mutations.SET_ERROR, null);
      })
      .catch(({ response, message }) => {
        this.context.commit(Mutations.SET_ERROR, { response, message });
      });
  }

  @Action
  [Actions.LOGOUT]() {
    return ApiService.post("/account/sign_out", {})
      .catch(() => {
        // Ignore errors — still clear local state
      })
      .finally(() => {
        this.context.commit(Mutations.PURGE_AUTH);
        this.context.commit(
          `ResidentsRegistrationsModule/${Mutations.SET_MY_RESIDENT_APPLICATION}`,
          null,
          { root: true }
        );
      });
  }
}
